import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import { isEmpty, map } from 'lodash';

import ResponsivePlayer from '../ResponsivePlayer';
import Slider from '../Slider';
import AnnotationMarker from '../AnnotationMarker';

import * as videoOps from '../../../api/Videos/video-operations';
import * as annotationOps from '../../../api/Annotations/annotation-operations';

import useTimeStore from '../ZustandProvider';

const YOUTUBE_URL_EXPRESSION = /^(https?:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+$/;
const FACEBOOK_URL_EXPRESSION = /^(https?:\/\/)?(?:www\.)?facebook\.com\/(?:(?:\w)*#!\/)?(?:pages\/)?(?:[\w-]*\/)*([\w-]*)/;
const TWITCH_URL_EXPRESSION = 'https://(?:clips|www).twitch.tv/(?:(?:[a-z]+)/clip/)?([a-zA-Z]+)';
const VideoAnnotationsMaps = ({
  selectedAnnotation,
  annotations,
  isNotesComponent,
  handleTimeVideo,
  handleDurationVideo,
  handleSizeVideo,
  handleSelectAnnotation,
  isMobile,
  videoSelected
}) => {
  const drawerWidth = useTimeStore(state => state.drawerWidth);
  const urlVideo = videoOps.getURL(videoSelected);

  const [player, setPlayer] = React.useState({});
  const [internalPlayer, setInternalPlayer] = React.useState({});
  const [durationVideo, setDurationVideo] = React.useState(0);
  const [videoTime, setVideoTime] = React.useState(0);
  const [isVideoReady, setVideoReady] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [checkUrl, setCheckUrl] = React.useState('none');
  const [playerWidth, setPlayerWidth] = React.useState(0);
  const [playerHeight, setPlayerHeight] = React.useState(0);
  const [annotationVisible, setAnnotationVisible] = React.useState(false);
  const [markerVisible, setMarkerVisible] = React.useState(false);
  const [readOnly, setReadOnly] = React.useState(true);

  const fileExtension = urlVideo.substring(urlVideo.lastIndexOf('.') + 1, urlVideo.length);

  const playerVars = {
    start: Math.round(videoTime * 1e-6)
  };

  const attributes = {
    autoPlay: false,
    muted: true
  };

  const configSourceVideo = {
    youtube: { playerVars },
    twitch: { playerVars },
    facebook: { playerVars },
    file: { attributes }
  };

  const syncroTimePlayerSlider = (elementClick, eventTime) => {
    if (elementClick === 'player') {
      if (isPlaying) handleTimeVideo(eventTime.playedSeconds);
      setVideoTime(eventTime.playedSeconds * 1e6);
    } else if (elementClick === 'slider') {
      player.seekTo(eventTime * 1e-6);
    }
  };

  const onVideoReady = event => {
    setPlayer(event);
  };

  const onPlayFunction = () => {
    setIsPlaying(true);
  };
  const onPauseFunction = () => {
    setIsPlaying(false);
  };
  const handleOnProgress = event => {
    syncroTimePlayerSlider('player', event);
  };

  const handleTimeSlider = (property, value) => {
    if (property === 'videoTime') {
      syncroTimePlayerSlider('slider', value);
    } else if (property === 'selectedAnnotation') {
      let newCount;
      map(annotations, (element, index) => {
        const idAnnotation = annotationOps.getId(element);
        const newAnnotation = annotationOps.getId(value);
        if (idAnnotation === newAnnotation) {
          newCount = index;
        }
      });
      setMarkerVisible(true);
      setAnnotationVisible(true);
      handleSelectAnnotation(value, newCount);
    }
  };

  const showAnnotation = () => {
    setAnnotationVisible(annotationVisible);
    setReadOnly(readOnly);
  };

  const checkSourceVideo = url => {
    let sourceVideo;
    const patronYoutube = new RegExp(YOUTUBE_URL_EXPRESSION);
    const patronFacebook = new RegExp(FACEBOOK_URL_EXPRESSION);
    const patronTwitch = new RegExp(TWITCH_URL_EXPRESSION);
    if (patronYoutube.test(url)) sourceVideo = 'youtube';
    if (patronFacebook.test(url)) sourceVideo = 'facebook';
    if (patronTwitch.test(url)) sourceVideo = 'twitch';
    if (fileExtension === 'MP4' || fileExtension === 'mp4') sourceVideo = 'file';
    return sourceVideo;
  };
  React.useEffect(() => {
    const check = checkSourceVideo(urlVideo);
    setCheckUrl(check);
  }, [urlVideo]);

  React.useEffect(() => {
    if (!isEmpty(player) && !isVideoReady) {
      const internalPlayerVideo = player.getInternalPlayer();
      if (checkUrl === 'youtube') {
        const duration = player.getDuration();
        const sizePlayer = internalPlayerVideo.getSize();
        setDurationVideo(duration * 1e6);
        handleDurationVideo(duration);
        setPlayerWidth(sizePlayer.width);
        setPlayerHeight(sizePlayer.height);
        handleSizeVideo(sizePlayer.width, sizePlayer.height);

        setInternalPlayer(internalPlayerVideo);
        internalPlayerVideo.pauseVideo();
      }

      if (checkUrl === 'twitch') {
        const duration = internalPlayerVideo.getVideo();
        setDurationVideo(duration * 1e3);
        handleDurationVideo(duration);
        setInternalPlayer(internalPlayerVideo);
        internalPlayerVideo.pause();
      }

      if (checkUrl === 'file') {
        const widthPlayer = internalPlayerVideo.clientWidth;
        const heightPlayer = internalPlayerVideo.clientHeight;
        const duration = player.getDuration();
        setDurationVideo(duration * 1e6);
        handleDurationVideo(duration);
        setInternalPlayer(internalPlayerVideo);
        setPlayerWidth(widthPlayer);
        setPlayerHeight(heightPlayer);
      }
      const startTime = annotationOps.getVideoTime(selectedAnnotation);
      syncroTimePlayerSlider('slider', startTime);

      setVideoReady(true);
    }
  }, [player]);

  React.useEffect(() => {
    const time = annotationOps.getVideoTime(selectedAnnotation);
    if (isVideoReady && !isPlaying) {
      player.seekTo(time * 1e-6);
    }
  }, [selectedAnnotation]);

  React.useEffect(() => {
    if (!isEmpty(internalPlayer) && checkUrl === 'youtube') {
      const sizePlayer = internalPlayer.getSize();
      if (drawerWidth !== 100 / 3) {
        const width = sizePlayer.width * ((3 * drawerWidth) / 100);
        const height = sizePlayer.height * ((3 * drawerWidth) / 100);
        setPlayerWidth(width);
        setPlayerHeight(height);
      }
      if (drawerWidth === 100 / 3) {
        setPlayerWidth(sizePlayer.width);
        setPlayerHeight(sizePlayer.height);
      }
    }
  }, [drawerWidth]);

  React.useEffect(() => {
    if (!isEmpty(annotations) && !isPlaying && isVideoReady) {
      let isMatchTime = false;
      map(annotations, element => {
        const timeAnnotation = annotationOps.getVideoTime(element);
        if (videoTime === timeAnnotation) {
          setMarkerVisible(true);
          setAnnotationVisible(true);
          isMatchTime = true;
        }
      });
      if (!isMatchTime) {
        setMarkerVisible(false);
        setAnnotationVisible(false);
      }
    }
  }, [videoTime]);

  if (!isEmpty(urlVideo))
    return (
      <>
        <Row>
          <Col>
            <ResponsivePlayer
              url={urlVideo}
              configPlayer={configSourceVideo}
              showControls
              playing={checkUrl !== 'file'}
              onProgressFunction={handleOnProgress}
              onPlayFunction={onPlayFunction}
              onVideoReadyFunction={onVideoReady}
              onVideoPauseFunction={onPauseFunction}
              isNotesComponent={isNotesComponent}
            />

            <AnnotationMarker
              showAnnotation={showAnnotation}
              selectedAnnotation={selectedAnnotation}
              markerVisible={markerVisible}
              clickadivWidth={playerWidth}
              clickadivHeight={playerHeight}
            />
          </Col>
        </Row>
        <br />
        <Slider
          domain={durationVideo}
          isVideoSynchronizer
          isVideoVisualizer
          videoTime={videoTime}
          changeVideoContainerStateProperty={handleTimeSlider}
          annotations={annotations}
          isNotesComponent={isNotesComponent}
          isMobile={isMobile}
        />
      </>
    );
  return '';
};

VideoAnnotationsMaps.defaultProps = {
  annotations: [],
  isNotesComponent: true,
  handleTimeVideo: () => {},
  handleSizeVideo: () => {},
  isMobile: false
};

VideoAnnotationsMaps.propTypes = {
  selectedAnnotation: PropTypes.object.isRequired,
  annotations: PropTypes.array.isRequired,
  isNotesComponent: PropTypes.bool,
  handleTimeVideo: PropTypes.func,
  handleDurationVideo: PropTypes.func.isRequired,
  handleSizeVideo: PropTypes.func,
  handleSelectAnnotation: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  videoSelected: PropTypes.object.isRequired
};

export default VideoAnnotationsMaps;
